import React, { useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ViewModeOption } from 'components/modals';

// ============================================================================
// REPORT ITEMS (Planned)
// ============================================================================

const PLANNED_REPORTS = [
  { id: 'addon-summary', title: 'Add-On Request Summary', description: 'Summary of all add-on requests by status, customer, and date range.', icon: <IconSystem.file size={28} /> },
  { id: 'loan-mapping', title: 'Loan Mapping Report', description: 'Mapping details with GL allocation breakdown and eligibility calculations.', icon: <IconSystem.list size={28} /> },
  { id: 'closing-settlement', title: 'Closing Settlement Report', description: 'Settlement details for closed loan mappings with P&I breakdown.', icon: <IconSystem.checkCircle size={28} /> },
  { id: 'combined-txn', title: 'Combined Transaction Report', description: 'Combined closing and new lot transactions with net cash flow analysis.', icon: <IconSystem.copy size={28} /> },
  { id: 'approval-audit', title: 'Approval Audit Trail', description: 'Complete audit trail of all approval actions with remarks and timestamps.', icon: <IconSystem.lock size={28} /> },
  { id: 'gold-rate-history', title: 'Gold Rate History', description: 'Historical gold rate trends with activation timeline.', icon: <IconSystem.chart size={28} /> },
];

// ============================================================================
// MAIN SCREEN
// ============================================================================

const ReportsPlaceholderScreen: React.FC = () => {
  const [viewMode, setViewMode] = useState<string>('list');
  const [searchQuery, setSearchQuery] = useState('');

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View', disabled: true },
    { id: 'calendar', icon: <IconSystem.calendar size={16} />, label: 'Calendar View', tooltip: 'Calendar View', disabled: true },
  ];

  const filteredReports = PLANNED_REPORTS.filter(
    (r) =>
      !searchQuery ||
      r.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="module-master-container">
      <ModuleHeader
        visible={true}
        title="Reports"
        icon={<IconSystem.trendingUp size={20} />}
        description="LMMS reporting and analytics."
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search reports...' }}
        viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (mode) => setViewMode(mode) }}
      />

      <div className="module-content">
        <div className="data-grid-container" style={{ padding: '1rem' }}>
          {/* Coming Soon Banner */}
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '2rem 1rem',
            background: 'rgba(59, 130, 246, 0.05)', borderRadius: '0.75rem', border: '1px dashed var(--border-color)', marginBottom: '1.5rem',
          }}>
            <IconSystem.chart size={48} style={{ color: 'var(--primary)', marginBottom: '0.75rem', opacity: 0.6 }} />
            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 0.25rem 0' }}>
              Reports — Coming Soon
            </h2>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: 0, textAlign: 'center', maxWidth: '32rem' }}>
              The following reports are planned for the LMMS reporting module. They will be available once the backend report generation APIs are implemented.
            </p>
          </div>

          {/* Planned Reports Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '0.75rem' }}>
            {filteredReports.map((report) => (
              <div
                key={report.id}
                style={{
                  padding: '1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)',
                  background: 'var(--bg-secondary)', opacity: 0.7, cursor: 'not-allowed',
                  display: 'flex', gap: '0.75rem', alignItems: 'flex-start',
                }}
              >
                <div style={{ color: 'var(--text-muted)', flexShrink: 0, marginTop: '0.1rem' }}>
                  {report.icon}
                </div>
                <div>
                  <h3 style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.25rem 0' }}>
                    {report.title}
                  </h3>
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: 0, lineHeight: 1.4 }}>
                    {report.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {filteredReports.length === 0 && (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
              No reports match your search.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportsPlaceholderScreen;
